@use "../../styles/breakpoint.module" as *;

$vertical-row-span: 5;
$horizontal-row-span: 4;

.card {
    position: relative;
    display: grid;
    grid-template-rows: subgrid;
    padding: var(--spacer-16) var(--spacer-16) var(--spacer-24);
    border: 1px solid var(--gray-300);

    &:has(a) {
        cursor: pointer;

        &:hover .picture {
            scale: 1.02;
        }
    }

    // skeleton mode!
    &[inert] {
        cursor: default;

        :is(.title, .sku, .facetLabel, .facetValue, .price, .picture) {
            background-color: var(--gray-300);
            color: var(--gray-300);
        }

        :is(.facetLabel, .facetValue, .price) {
            width: 100px;
            max-width: 100%;
        }

        .image {
            display: none;
        }

        &:hover {
            .picture {
                scale: 1;
            }
        }
    }

    @media print {
        break-inside: avoid;
    }
}

.cardVertical {
    composes: card;
    gap: var(--spacer-24);
    grid-row: span $vertical-row-span;
    grid-template-areas:
        "header"
        "image"
        "facets"
        "facets"
        "price";
}

.cardHorizontal {
    composes: card;
    gap: var(--spacer-16) var(--spacer-24);
    grid-row: span $horizontal-row-span;
    grid-template-columns: 2fr 3fr;
    grid-template-areas:
        "image header"
        "image facets"
        "image facets"
        "image price";

    .picture {
        aspect-ratio: 4 / 3;
    }
}

.cardHorizontalLarge {
    composes: cardHorizontal;
    border-color: transparent;
    grid-template:
        "image ." 1fr
        "image header" auto
        "image facets" auto
        "image ." 1fr
        / 3fr 4fr;

    @media ($below-mobile) {
        grid-template:
            "header"
            "image"
            "facets"
            / 1fr;
        gap: var(--spacer-24);
        padding: var(--spacer-32);
    }
}

.header {
    grid-area: header;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto 1fr;
    align-items: start;
    gap: var(--spacer-4);

    .cardHorizontalLarge & {
        grid-template-columns: 1fr;
        gap: var(--spacer-16);
    }
}

.detailLink {
    grid-column: 1;
    margin-block: var(--spacer-8) var(--spacer-16);
    font: var(--font-text-link-14);
    text-decoration: underline;
}

.sku {
    grid-column: 1;
    font: var(--font-eyebrow-14);
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.title {
    grid-column: 1;
    font: var(--font-h-24);

    .cardHorizontalLarge & {
        font: var(--font-h-40);
    }
}

.badgeFloated {
    position: absolute;
    top: var(--spacer-16);
    left: var(--spacer-16);
    z-index: 1;
}

.badgeInline {
    justify-self: start;
    grid-column: 1;
}

.favoriteButton {
    grid-column: 2;
    grid-row: 1 / span 2;
    padding: var(--spacer-16);
    translate: var(--spacer-16) calc(var(--spacer-16) * -1);
}

.favoriteSvg {
    width: var(--spacer-24);
    height: var(--spacer-24);
}

.favoriteButton:not([data-is-favorite]):hover .favoriteSvg {
    --heart-fill: 75%;
}

.picture {
    grid-area: image;
    align-self: center;
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 5 / 3;
    overflow: hidden;
    transition: scale 0.2s;

    @media print {
        aspect-ratio: 7 / 3;
    }
}

// object-fit contain didn't look good on skinny cards;
// object-fit cover didn't look good on wider cards
.image {
    position: absolute;
    top: 0;
    left: 50%;
    translate: -50%;
    height: 100%;
    width: auto;
    max-width: none;
}

.facetList {
    composes: listReset from "../../styles/composable/utility.module.css";
    display: grid;
    grid-area: facets;
    grid-template-rows: subgrid;
    grid-template-columns: 1fr 1fr;

    .cardHorizontalLarge & {
        grid-template-rows: auto;
        gap: var(--spacer-8);
    }
}

.facetLabel {
    color: var(--gray-700);
    font: var(--font-eyebrow-14);
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.facetValue {
    font: var(--font-nav-14);
}

.price {
    grid-area: price;
    font: var(--font-p-16);
}
